/**
 * Рейтинг бренда и отзывы о призах.
 *
 * Оценку ставит только тот, кто подтвердил получение приза, — поэтому рядом с
 * числом всегда стоит, сколько призов выдано. «5,0» по одному отзыву и по
 * тремстам — разные вещи, и скрывать эту разницу значит врать цифрой.
 */
import type { Rating, Review } from '@allswipes/client';
import { Icon, fmtShortDay, num } from '@allswipes/ui';

import { usePlural, useT } from '@allswipes/i18n';

/** Средняя оценка ×100 как «4,2». */
export function ratingValue(rating: Rating): string {
  const whole = Math.floor(rating.value / 100);
  const frac = Math.round((rating.value % 100) / 10);
  return frac > 0 ? `${whole},${frac}` : String(whole);
}

export function Stars({ value, size = 13 }: { value: number; size?: number }) {
  // Половинки не рисуем: звезда либо есть, либо нет, а точное число стоит
  // рядом цифрой. Дробная звезда читается хуже, чем «4,2».
  const full = Math.round(value / 100);
  return (
    <span className="stars" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((i) => (
        <Icon key={i} name="star" size={size} className={i <= full ? '' : 'off'} />
      ))}
    </span>
  );
}

/** Компактный рейтинг: число, звёзды и количество отзывов. */
export function RatingLine({ rating, prizes }: { rating?: Rating; prizes?: number }) {
  const t = useT();
  const plural = usePlural();
  if (!rating || rating.count === 0) {
    return (
      <span className="rating">
        <span className="faint" style={{ fontSize: 12.5 }}>
          {prizes ? t('Призы выдавались, отзывов пока нет') : t('Пока нет выданных призов')}
        </span>
      </span>
    );
  }
  return (
    <span className="rating">
      <span className="val">{ratingValue(rating)}</span>
      <Stars value={rating.value} />
      <span style={{ fontSize: 12.5 }}>
        {num(rating.count)} {plural(rating.count, 'отзыв', 'отзыва', 'отзывов')}
        {prizes
          ? ` · ${t('{n} {prizes} выдано', { n: num(prizes), prizes: plural(prizes, 'приз', 'приза', 'призов') })}`
          : ''}
      </span>
    </span>
  );
}

/** Распределение оценок — чтобы средняя не скрывала двойки. */
export function RatingBars({ rating }: { rating: Rating }) {
  const max = Math.max(...rating.stars, 1);
  return (
    <div className="ratebars">
      {[5, 4, 3, 2, 1].map((star) => {
        const count = rating.stars[star - 1] ?? 0;
        return (
          <div key={star} className="rb">
            <span>{star} ★</span>
            <span className="bar">
              <i style={{ width: `${Math.round((100 * count) / max)}%` }} />
            </span>
            <span className="mono">{num(count)}</span>
          </div>
        );
      })}
    </div>
  );
}

export function ReviewList({ reviews }: { reviews: Review[] }) {
  const t = useT();
  if (reviews.length === 0) {
    return (
      <p className="sub">
        {t('Отзывов пока нет. Их пишут только те, кто подтвердил получение приза, — поэтому их всегда меньше, чем участников.')}
      </p>
    );
  }
  return (
    <div>
      {reviews.map((review, i) => (
        <div key={i} className="review">
          <div className="top">
            <Stars value={review.rating * 100} size={12} />
            <span className="who">
              {review.author} · {review.prize_label} · {fmtShortDay(review.created_at)}
            </span>
          </div>
          {review.text && <div className="txt">{review.text}</div>}
        </div>
      ))}
    </div>
  );
}
